import { css } from '@emotion/react'
import React, { ReactNode, useMemo } from 'react'

import { Link } from '@emico/link'
import { overrideOptional } from '@emico/overrides'

import { H1, H2, H3, H4, H5, H6 } from './Heading'
import Image from './Image'
import ListItem from './ListItem'
import OrderedList from './OrderedList'
import Table from './Table'
import TableDataCell from './TableDataCell'
import TableHeaderCell from './TableHeaderCell'
import TableRow from './TableRow'
import UnorderedList from './UnorderedList'

export type ComponentMapping = {
  [nodeName: string]: (
    node: ChildNode,
    index: number,
    children: ReactNode,
  ) => JSX.Element
}
export const HTMLContentLink = overrideOptional(Link)

interface Props {
  html: string
  componentMapping?: ComponentMapping
  allowStyles?: boolean
  removeSingleParagraphWrapper?: boolean
}

// Simple component mapping configuration
const simpleComponentMap: { [key: string]: React.ElementType } = {
  DIV: 'div',
  P: 'p',
  SPAN: 'span',
  STRONG: 'strong',
  EM: 'em',
  // H1-H6 are mapped to components from ./Heading
  H1,
  H2,
  H3,
  H4,
  H5,
  H6,
  UL: UnorderedList,
  OL: OrderedList,
  LI: ListItem,
  TABLE: Table,
  TBODY: 'tbody',
  THEAD: 'thead',
  TR: TableRow,
  TH: TableHeaderCell,
  TD: TableDataCell,
}

export function getHTMLContentCSSProp(node: ChildNode, allowStyles: boolean) {
  if (!(node instanceof Element) || !allowStyles) {
    return {}
  }

  const style = node?.getAttribute('style')
  if (style) {
    return {
      css: css`
        ${style}
      `,
    }
  }
  return {}
}

/**
 * This recursive functions loops over HTML ChildNode's and maps them to React
 * Components.
 * @param nodes HTML ChildNode's to map to React components
 * @param componentMapping Custom mapping configuration
 */
function parseChildNodes(
  nodes: NodeListOf<ChildNode>,
  componentMapping: ComponentMapping | undefined,
  allowStyles: boolean,
): ReactNode {
  // The resulting React components will be stored in this Array
  const reactComponents = []

  for (let i = 0; i < nodes.length; i += 1) {
    const node = nodes[i]
    const { nodeName } = node

    // If the component name exists in the componentMapping prop, than that
    // should be prioritized
    if (componentMapping?.[nodeName]) {
      reactComponents.push(
        componentMapping[nodeName](
          node,
          i,
          // Eagerly loop over the children to make component mapping
          // easier.
          parseChildNodes(node.childNodes, componentMapping, allowStyles),
        ),
      )
      continue
    }

    switch (nodeName) {
      // Plain text in HTML
      case '#text':
        reactComponents.push(node.nodeValue)
        break
      // Images should be rendered using the Image component because to
      // make sure it goes trough any CDN.
      case 'IMG': {
        const img = node as HTMLImageElement
        reactComponents.push(
          <Image
            key={i}
            url={img.src}
            alt={img.alt}
            width={img.width || undefined}
            height={img.height || undefined}
            {...getHTMLContentCSSProp(node, allowStyles)}
          />,
        )
        break
      }
      // Anchor's should be renderd using the Link component to make
      // internal links work correctly and also for safer external links.
      case 'A': {
        const anchor = node as HTMLAnchorElement
        reactComponents.push(
          <HTMLContentLink
            key={i}
            to={anchor.href}
            target={anchor.target || undefined}
            analyticsContext="htmlcontent"
            analyticsName="anchor"
            {...getHTMLContentCSSProp(node, allowStyles)}
          >
            {parseChildNodes(node.childNodes, componentMapping, allowStyles)}
          </HTMLContentLink>,
        )
        break
      }
      default:
        // Finally go trough the simpleComponentMap and map those
        // components without any attributes
        if (simpleComponentMap[nodeName]) {
          reactComponents.push(
            React.createElement(
              simpleComponentMap[nodeName],
              {
                key: i,
                ...getHTMLContentCSSProp(node, allowStyles),
              },
              parseChildNodes(node.childNodes, componentMapping, allowStyles),
            ),
          )
        }

      // All other tags should be ignored, we don't want to end up with
      // <script> tags in our content.
    }
  }
  return reactComponents
}

const HTMLContent = ({
  html,
  componentMapping,
  allowStyles,
  removeSingleParagraphWrapper,
}: Props) => {
  const elements = useMemo(() => {
    // Initializes a DOMParser, and parse the HTML
    const domparser = new DOMParser()
    const doc = domparser.parseFromString(html, 'text/html')

    const nodes = doc.body.childNodes

    if (removeSingleParagraphWrapper) {
      // Often HTML with a single P also has spaces before or after the P
      const nodesWithoutEmptyText = Array.from(nodes).filter(
        (o) => !(o.nodeName === '#text' && o.nodeValue?.trim() === ''),
      )

      if (
        nodesWithoutEmptyText.length === 1 &&
        nodesWithoutEmptyText[0].nodeName === 'P'
      ) {
        return parseChildNodes(
          nodes[0].childNodes,
          componentMapping,
          Boolean(allowStyles),
        )
      }
    }

    // Map the documents children to React components
    return parseChildNodes(nodes, componentMapping, Boolean(allowStyles))
  }, [allowStyles, componentMapping, html, removeSingleParagraphWrapper])

  return <>{elements}</>
}

export default HTMLContent
